import { useContext, useState, useEffect, SetStateAction } from 'react';
import { Accordion, Button, Col, Container, Form, Modal, Row, Nav } from 'react-bootstrap';

import useAlert from '../../Alerts/useAlert';
import PrivilegeContext from '../../Contexts/PrivilegeContext';
import DataTable from '../../DataTable/datatable.component';
import { RequiredDialogInput, RequiredDialogIntegerInput, DialogSelect, DialogInput } from '../../DialogInputs';
import { DraggableModalDialog } from '../DraggableModalDialog';
import { useHttpUtils } from '../../Utilities/useHttpUtils';
import { handleInputChange } from '../../Utilities/DialogUtils';
import CommentDialog from '../CommentDialog';
import { RecipeToPackageTypeEditDialog } from './RecipeToPackageTypeEditDialog';
import { PackageType } from '../../Types/PackageType';
import { BusinessUnit } from '../../Types/BusinessUnit';
import { RecipeToPackageType } from '../../Types/RecipeToPackageType';

interface PackageTypeAreaProps {
    packageType: PackageType,
    setPackageType: React.Dispatch<SetStateAction<PackageType>>,
    businessUnits: BusinessUnit[]
}

function PackageTypeArea({ packageType, setPackageType, businessUnits }: PackageTypeAreaProps) {
    const { fetchDataAuthenticated } = useHttpUtils();
    const [fieldErrors, setFieldErrors] = useState<Partial<PackageType>>({});
    const [validated, setValidated] = useState(true);
    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const { setAlert } = useAlert();
    const { canConfigure } = useContext(PrivilegeContext);

    const onChange = (e: any) => {
        handleInputChange(e, packageType, setPackageType, fieldErrors, setFieldErrors);
    };

    const handleSave = (event: { preventDefault: () => void; stopPropagation: () => void; currentTarget: any; }) => {
        event.preventDefault();
        event.stopPropagation();

        const form = event.currentTarget;

        if (form.checkValidity() === true) {
            setShowCommentDialog(true);
        }

        setValidated(true);
    };

    const handleCommentSave = (comment: string | undefined) => {

        packageType.comment = comment;
        let url = `api/v1/packageType`;
        if (packageType.id != null) {
            url += `/${packageType.id}`;
        }

        fetchDataAuthenticated(url, "POST", JSON.stringify(packageType))
            .then(response => {
                if (!response.ok) {
                    response.text().then(error => {
                        setAlert("danger", "Failed to delete", !!error ? error : "An unexpected error occured");
                    });
                } else {
                    setAlert("success", "Updated", "Package Type has successfully been updated");
                }
                return response.json();
            })
            .then(data => {
                setFieldErrors({});
                setPackageType({ ...data });
            })
            .catch(error => {
                alert("There was a problem! " + error);

                setFieldErrors({
                    ...fieldErrors,
                    ...error.errors
                });

                for (const field in error.errors) {
                    let fieldDOM: HTMLInputElement | null = document.querySelector("[name=" + field + "]");
                    fieldDOM?.setCustomValidity(error.errors[field]);
                }
            });
    };

    const deletePackageType = (packageType: PackageType) => {
        let promise = new Promise((resolve, reject) => {
            let url = `api/v1/packageType/${packageType.id}`;

            fetchDataAuthenticated(url, "DELETE")
                .then(response => {
                    if (!response.ok) {
                        reject(response);
                        response.text().then(error => {
                            console.log(error);
                            setAlert("danger", "Failed to delete", !!error ? error : "An unexpected error occured");
                        });
                    } else {
                        setAlert("success", "Deleted", "Package Type has successfully been deleted");
                    }
                    resolve(response);
                })
                .catch(error => {
                    alert("There was a problem! " + error);
                });
        });
        return promise;
    };

    const handleDelete = () => {
        deletePackageType(packageType);
    };


    return (
        <>
            <Container>
                <Row>
                    <Form noValidate validated={validated} onSubmit={handleSave} id="packageTypeSummaryForm">
                        <fieldset disabled={!canConfigure}>
                            <RequiredDialogInput controlId="formPackageTypeName" title="Name" name="name" value={packageType.name} error={fieldErrors.name} onChange={onChange}  />
                            <DialogInput controlId="formPackageTypeAlternativeName" title="Alternative Name" name="alternativeName" value={packageType.alternativeName} error={fieldErrors.alternativeName} onChange={onChange} />
                            <DialogInput controlId="formPackageTypeDisplayName" title="Display Name" name="displayName" value={packageType.displayName} error={fieldErrors.displayName} onChange={onChange} />
                            <RequiredDialogIntegerInput controlId="formPackageTypeUnitsPerPackage" title="Units Per Package" placeholder={null} name="unitsPerPackage" value={packageType.unitsPerPackage} error={fieldErrors.unitsPerPackage} onChange={onChange} />
                            <RequiredDialogIntegerInput controlId="formPackageTypeUnitWeight" title="Unit Weight" placeholder={null} name="unitWeight" value={packageType.unitWeight} error={fieldErrors.unitWeight} onChange={onChange} />
                            <DialogSelect controlId="formPackageTypeWeightUoM" title="Weight UoM" name="weightUoM" value={packageType.weightUoM} error={fieldErrors.weightUoM} onChange={onChange}>
                                <option value=""></option>
                                <option value="kg">kg</option>
                                <option value="lb">lb</option>
                            </DialogSelect>
                            <DialogSelect controlId="formPackageTypeBusinessUnit" title="Business Unit" name="businessUnitId" value={packageType.businessUnitId} error={fieldErrors.businessUnitId} onChange={onChange}>
                                <option value="" key={0} >Please Select...</option>
                                {businessUnits.map((bu) => <option value={bu.businessUnitId} key={bu.businessUnitId}>{bu.name}</option>)}
                            </DialogSelect>
                        </fieldset>
                    </Form>
                </Row>
                <Row>
                    <Col>
                        <Button variant="danger" className="float-start" type="button" disabled={!canConfigure} onClick={handleDelete}>
                            Delete
                        </Button>
                        <Button variant="primary" className="float-end" type="submit" form="packageTypeSummaryForm" disabled={!canConfigure}>
                            Save
                        </Button>
                    </Col>
                </Row>
            </Container>

            <CommentDialog showDialog={showCommentDialog} closeDialog={() => setShowCommentDialog(false)} onSave={handleCommentSave} />
        </>
    );
}

interface RecipeToPackageTypesTableProps {
    onRowClick: (dialogData: RecipeToPackageType) => void,
    packageTypeId: number,
    refresh: any
}

function RecipeToPackageTypesTable({ onRowClick, packageTypeId, refresh }: RecipeToPackageTypesTableProps) {
    const { fetchDataAuthenticated } = useHttpUtils();
    const [loading, setLoading] = useState(false);
    const [errored, setErrored] = useState(false);
    const [tableData, setTableData] = useState([]);

    useEffect(() => {
        const getRecipeToPackageTypes = function () {
            if (packageTypeId > 0) {
                setLoading(true);
                let url = `api/v1/recipeToPackageType/?packageTypeId=${packageTypeId}`;

                fetchDataAuthenticated(url)
                    .then(response => {
                        if (response.ok) {
                            return response.json();
                        }
                        throw new Error('Something went wrong.');
                    })
                    .then(data => {
                        setTableData(data);
                        setErrored(false);
                    })
                    .catch(error => {
                        setErrored(true);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        };

        getRecipeToPackageTypes();
    }, [fetchDataAuthenticated, packageTypeId, refresh]);

    let tableColumns = [
        {
            Header: 'Material Code',
            accessor: 'materialCode',
            filter: 'includesString',
            Cell: (props: any) => {
                return (<Nav><Nav.Link onClick={() => { onRowClick(props.row.original); }}>{props.value}</Nav.Link></Nav>);
            }
        },
        {
            Header: 'Recipe',
            accessor: 'recipeName',
            filter: 'includesString',
        },
        {
            Header: 'Package Type',
            accessor: 'packageTypeName',
            filter: 'includesString',
        },
        {
            Header: 'Is Default',
            accessor: 'isDefault',
            filter: 'includesString',
            Cell: (props: any) => <div>{props.value.toString()}</div>
        },
    ];

    const getRowProps = (row: any) => {
        if (row.original.isDeleted)
            return { className: "deleted" };

        return {};
    };

    if (loading) {
        return <p><em>Loading...</em></p>;
    }

    if (errored) {
        return <p>There was a problem</p>;
    }

    if (tableData.length === 0) {
        return <></>;
    }

    return <DataTable tableColumns={tableColumns} tableData={tableData} initialState={{ pageSize: 50 }} getRowProps={getRowProps} />;
}

interface RecipeToPackageTypesAreaProps {
    packageType: PackageType
}

function RecipeToPackageTypesArea({ packageType }: RecipeToPackageTypesAreaProps) {
    const [refreshList, setRefreshList] = useState(0);
    const [showDialog, setShowDialog] = useState(false);
    const [dialogData, setDialogData] = useState<RecipeToPackageType>({ packageTypeId: packageType.id, recipeId: null, materialCode: "", isDefault: false });

    const { canConfigure } = useContext(PrivilegeContext);

    const showEditDialog = (dialogData: any) => {
        setDialogData(dialogData);
        setShowDialog(true);
    };

    const showNewDialog = () => {
        setDialogData({ packageTypeId: packageType.id, recipeId: null, materialCode: "", isDefault: false });
        setShowDialog(true);
    };

    const closeDialog = () => {
        setShowDialog(false);
        setRefreshList(refreshList + 1);
    };

    return (
        <Container fluid="md" className="mb-3">
            <Row>
                <Col>
                    <RecipeToPackageTypesTable onRowClick={showEditDialog} packageTypeId={packageType.id} refresh={refreshList} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Button className="float-end" variant="primary" onClick={showNewDialog} disabled={!canConfigure}>Add</Button>
                </Col>
            </Row>

            <RecipeToPackageTypeEditDialog dialogData={dialogData} setDialogData={setDialogData} showDialog={showDialog} closeDialog={closeDialog} />

        </Container>
    );
}

interface PackageTypeEditDialogProps {
    dialogData: PackageType,
    setDialogData: React.Dispatch<SetStateAction<PackageType>>,
    showDialog: boolean,
    closeDialog: () => void,
    businessUnits: BusinessUnit[]
}

export function PackageTypeEditDialog({ dialogData, setDialogData, showDialog, closeDialog, businessUnits }: PackageTypeEditDialogProps) {
    return (
        <Modal dialogAs={DraggableModalDialog} handle=".packageType-header" className="detail-dialog" show={showDialog} onHide={closeDialog} backdrop="static">
            <Modal.Header closeButton className="packageType-header">
                <Modal.Title>Package Type - {dialogData.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Accordion defaultActiveKey={['summary', 'recipeToPackageType']} alwaysOpen>
                    <Accordion.Item eventKey="summary">
                        <Accordion.Header>Package Type</Accordion.Header>
                        <Accordion.Body>
                            <PackageTypeArea packageType={dialogData} setPackageType={setDialogData} businessUnits={businessUnits} />
                        </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="recipeToPackageType">
                        <Accordion.Header>Recipe to Package Type</Accordion.Header>
                        <Accordion.Body>
                            <RecipeToPackageTypesArea packageType={dialogData} />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeDialog}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}